import React, { useState, useEffect } from "react";
import { supabase } from "../../../lib/supabase";
import { Plus } from "lucide-react";

interface PMSScope {
  id: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
}

export function PMSScopes() {
  const [scopes, setScopes] = useState<PMSScope[]>([]);
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<Partial<PMSScope>>({});

  useEffect(() => {
    loadScopes();
  }, []);

  const loadScopes = async () => {
    try {
      const { data, error: queryError } = await supabase
        .from("pms_scopes")
        .select("*")
        .order("name");

      if (queryError) throw queryError;

      setScopes(data || []);
    } catch (err) {
      setError("Failed to load PMS scopes");
      console.error("Error loading scopes:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    try {
      if (!formData.name?.trim()) {
        throw new Error("Name is required");
      }

      if (isEditing) {
        const { error: updateError } = await supabase
          .from("pms_scopes")
          .update({
            name: formData.name,
            description: formData.description,
            updated_at: new Date().toISOString(),
          })
          .eq("id", isEditing);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from("pms_scopes")
          .insert([
            {
              name: formData.name,
              description: formData.description,
            },
          ]);

        if (insertError) throw insertError;
      }

      await loadScopes();
      handleCancel();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save scope");
      console.error("Error saving scope:", err);
    }
  };

  const handleEdit = (scope: PMSScope) => {
    setIsEditing(scope.id);
    setFormData(scope);
    setIsCreating(false);
  };

  const handleDelete = async (id: string) => {
    try {
      const { error: deleteError } = await supabase
        .from("pms_scopes")
        .delete()
        .eq("id", id);

      if (deleteError) throw deleteError;

      await loadScopes();
    } catch (err) {
      setError("Failed to delete scope");
      console.error("Error deleting scope:", err);
    }
  };

  const handleCancel = () => {
    setIsEditing(null);
    setIsCreating(false);
    setFormData({});
    setError("");
  };

  if (loading) {
    return (
      <div className="extranet-container">
        <div className="extranet-container-loading">
          <div className="text-gray-500">Loading PMS scopes...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="extranet-container">
      <div className="extranet-container-header">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setIsCreating(true)}
            className="btn-primary inline-flex items-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add New Scope
          </button>
        </div>
      </div>

      <div className="extranet-container-body">
        {error && <div className="mb-4 p-4 bg-red-50">{error}</div>}

        {(isEditing || isCreating) && (
          <form onSubmit={handleSubmit} className="mb-8 bg-gray-50">
            <div className="space-y-4">
              <div>
                <label className="form-label">Name</label>
                <input
                  type="text"
                  value={formData.name || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="form-input"
                  required
                />
              </div>

              <div>
                <label className="form-label">Description</label>
                <textarea
                  value={formData.description || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="form-input"
                  rows={3}
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {isEditing ? "Save Changes" : "Create Scope"}
                </button>
              </div>
            </div>
          </form>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="table-header">Name</th>
                <th className="table-header">Description</th>
                <th className="table-header">Created</th>
                <th className="table-header">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {scopes.map((scope) => (
                <tr key={scope.id} className="table-row">
                  <td className="table-cell">{scope.name}</td>
                  <td className="table-cell">{scope.description}</td>
                  <td className="table-cell">
                    {new Date(scope.created_at).toLocaleDateString()}
                  </td>
                  <td className="table-cell">
                    <div className="flex space-x-4">
                      <button
                        onClick={() => handleEdit(scope)}
                        className="text-indigo-600 hover:text-indigo-800"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(scope.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {scopes.length === 0 && !isCreating && (
            <div className="text-center py-8">
              <p className="text-gray-500">
                No PMS scopes found. Add your first scope to get started.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
